import Box from '@mui/material/Box';
import React from 'react';
import { AdContext } from '../contexts/ads-context';
import { APP_BAR } from './alliance/alliance-tokens';
import { useIsMobileLayout } from './alliance/bar-controls';

/**
 * The page header row under the App Bar: a page's `headerContent` (filters,
 * agent pickers…), right-aligned on a white band that sticks under the bar.
 *
 * The App Bar shows the page title on desktop only, so on phones this row
 * also carries the title. With neither, nothing renders.
 *
 * @param routeTitle - Shown at the left on phones
 * @param headerContent - Controls shown at the right
 * @param wrapContentForMobile - Stack the content under the title on phones
 */
export function AppHeader(props: {
  routeTitle?: string;
  headerContent?: React.ReactNode;
  wrapContentForMobile?: boolean;
}) {
  // Context
  const { state } = React.useContext(AdContext);
  // Hooks
  const isMobile = useIsMobileLayout();

  const showTitle = isMobile && !!props.routeTitle;
  if (!props.headerContent && !showTitle) {
    return null;
  }

  return (
    <Box
      sx={{
        // Stick under the fixed ad banner and App Bar while there are
        // controls to keep in reach; a title-only row scrolls away
        position: props.headerContent ? 'sticky' : 'relative',
        top: props.headerContent
          ? {
              xs: state.bannerHeight + APP_BAR.heightMobile,
              md: state.bannerHeight + APP_BAR.heightDesktop,
            }
          : undefined,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #0000001f',
      }}
    >
      <Box
        sx={{
          width: '96%',
          mx: 'auto',
          minHeight: { xs: 48, md: 64 },
          py: 1,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: props.wrapContentForMobile
            ? { xs: 'stretch', md: 'center' }
            : 'center',
          gap: 2,
          flexDirection: props.wrapContentForMobile
            ? { xs: 'column', md: 'row' }
            : 'row',
        }}
      >
        {showTitle ? (
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: 18,
              fontWeight: 500,
              lineHeight: 1.3,
              color: '#052a63',
              flex: props.wrapContentForMobile ? 'none' : 1,
              minWidth: 0,
            }}
          >
            {props.routeTitle}
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        {props.headerContent}
      </Box>
    </Box>
  );
}
